<template>
  <div>
    <div id="header-container">
      <div class="container">
        <div class="row">
          <div class="col">
            <text-block content-id="blog-title" title="title" white="white" v-on:save="saveTitle" :content="post.titleHtml"/>
          </div>
        </div>
      </div>
    </div>
    <div class="container section">
      <div class="row" v-if="isContentManager()">
        <div class="col-auto">
          <Button :label="t('ADD')" @click="addLang" class="blue"/>
        </div>
        <div class="col">
          <Button :label="t('REMOVE')" @click="showRemoveConfirmation" class="blue"/>
        </div>
      </div>
      <div class="row">
        <div class="col-auto">
          <div class="text18-gray">{{ dateFormat(post.createdAt) }}</div>
        </div>
        <div class="col">
          <text-block content-id="blog-content" v-on:save="saveContent" :content="post.content"/>
        </div>
      </div>
    </div>
    <blog-creation-modal
        v-on:added-blog="newLangAdded"
        :existing-languages="post.availableLanguages"
        :group-id="post.blogGroupId"
    ></blog-creation-modal>
  </div>
</template>

<script setup>

import {nextTick, onBeforeUnmount, onMounted, ref, toRefs} from "vue";
import {useI18n} from "vue-i18n";
import {injectAxios, injectAuth} from "@/util/inject";
import EventBus from "@/util/eventBus";
import {useRouter} from "vue-router";
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";
import TextBlock from "@/views/components/TextBlock.vue";
import blogCreationModal from "@/views/components/modals/blogCreationModal.vue";
import {useToast} from "@/util/toastUtils";

const {dateFormat} = useDateFormat();
const axios = injectAxios();
const { t } = useI18n();
const toast = useToast();
const auth = injectAuth();
const router = useRouter();
const props = defineProps(["slug", "data"]);
const { slug, data } = toRefs(props);
const post = ref(
    {
      id: null,
      slug: "",
      title: "",
      titleHtml: "",
      content: "",
      lang: "",
      blogGroupId: null,
      availableLanguages: [],
    })

onMounted(() => {
  EventBus.$on("locale", switchLang);
  switchLang()
  showPost();
})

onBeforeUnmount(() => {
  EventBus.$off("locale", switchLang);
})

const isContentManager = () => {
  return auth.hasRole("CONTENT_MANAGER");
};

function showPost() {
  if (data.value) {
    post.value = data.value;
  } else if (slug.value) {
    getPost();
  }
}

async function getPost() {
  const resp = await axios.get("/client/blog?name=" + slug.value);
  post.value = resp.data;
}

async function addLang() {
  await nextTick()
  EventBus.$emit("show-blog-creation-modal");
}

const newLangAdded = (blog) => {
  post.value = blog;
  router.replace({name: "Blog", params: {slug: blog.slug}})
      .then();
  EventBus.$emit("switch-locale", blog.lang);
}

async function switchLang(locale) {
  if (!post.value.availableLanguages.includes(locale) || locale === post.value.lang
  ) {
    return;
  }
  const resp = await axios.get(
      `/client/blog/switch-lang?lang=${locale}&groupId=${post.value.id}`,
  );
  const availableLanguages = post.value.availableLanguages;
  post.value = resp.data;
  post.value.availableLanguages = availableLanguages;
  router
      .replace({name: "Blog", params: {slug: post.value.slug}})
      .then();
}

function getSlug(raw) {
  return raw
      .toLowerCase()
      .normalize("NFD")
      .trim()
      .replaceAll(/[–!*'();:@&=+$,/?#[\]]/g, "")
      .replaceAll(/\s+/g, "-") // Replace spaces with -
      .replaceAll(/--+/g, "-"); // Replace multiple - with single -
}

async function saveBlog() {
  const resp = await axios.put(
    "/client/blog/" + post.value.id,
    post.value,
  );
  const availableLanguages = post.value.availableLanguages;
  post.value = resp.data;
  post.value.availableLanguages = availableLanguages;
  toast.makeSuccessToast(t("SAVED"));
}

function extractTitle(str) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = str
  return tempDiv.textContent || tempDiv.innerHTML || ''
}

async function saveTitle(elem) {
  post.value.slug = getSlug(extractTitle(elem.value));
  post.value.title = extractTitle(elem.value);
  post.value.titleHtml = elem.value;
  await saveBlog();
  router
      .push({name: "Blog", params: {slug: post.value.slug}})
      .then();
}

function saveContent(elem) {
  post.value.content = elem.value;
  saveBlog();
}

function showRemoveConfirmation() {
  const data = {
    msg: t("REMOVE_BLOG_CONFIRM_MSG"),
    callback: remove,
  };
  EventBus.$emit("show-confirmation-modal", data);
}

async function remove() {
    await axios.delete(`/client/blog/${post.value.id}`);
    toast.makeSuccessToast(t("REMOVED_BLOG"));
    router.replace({name: "Home"}).then();
}
</script>

<style lang="scss" scoped>
#header-container {
  background:
    linear-gradient(rgba(0, 0, 150, 0.4), rgba(0, 0, 150, 0.4)),
    url("@/assets/saun.jpg") no-repeat center;
  background-size: cover;
  padding-top: 240px;
  padding-bottom: 146px;
  min-height: 664px;
}
</style>
